import type { Route } from 'vue-router'
import type { LinkConfig } from './config-objects/IConfiguration'
import type IConfiguration from './config-objects/IConfiguration'
import type IDocsPreference from './config-objects/IDocsPreference'
import type IExperiments from './config-objects/IExperiments'
import type {
  IAggregatorConfigSpecification,
  IDirectDebitPopUpSpecification,
  IGeneralConfigSpecification,
  IJourneyConfigSpecification,
} from '@/configurations/config-objects/ConfigSpecification'
import type { ILinkConfigSpecification } from '@/configurations/config-objects/LinkConfigSpecification'
import type Routes from '@/constants/Routes'
import type { PaymentTypeEnum } from '@/services/enums/DLDCommonValuesEnum'
import type ProductEnum from '@/services/enums/ProductEnum'
import type Policy from '@/services/models/Policy'
import type { TenantConfigName } from '@/services/types/TenantConfigName'
import type ClaimSummary from '@/services/values/ClaimSummary'
import type PolicySummary from '@/services/values/PolicySummary'
import { LinkConfigActionShowTypes } from '@/configurations/config-objects/LinkConfigSpecification'
import LinkConfigFactory from '@/configurations/LinkConfigFactory'
import { PWAUtils } from '@/plugins/pwa-utils'
import RenewalStatusEnum from '@/services/enums/RenewalStatusEnum'
import { TierEnum, TierFeatureType } from '@/services/enums/TierEnum'
import Step from '@/view-models/Step'
import { TierFeature } from './config-objects/IConfiguration'

const linkFilterFunction = (l: ILinkConfigSpecification) => l.showOn.length === 0 || (PWAUtils.isMobile() && !!l.showOn.find(s => s === LinkConfigActionShowTypes.mobile)) || (!PWAUtils.isMobile() && !!l.showOn.find(s => s === LinkConfigActionShowTypes.desktop))

export default class ConfigurationLoader {
  static createConfiguration(general: IGeneralConfigSpecification, agg: IAggregatorConfigSpecification, journey: IJourneyConfigSpecification, experiments: IExperiments): IConfiguration {
    return {
      cancellationJourney: general.cancellationJourney,
      tenantConfigName: window.injectedEnv.FRONTEND_TENANT as TenantConfigName,
      tenantFriendlyName: window.injectedEnv.FRONTEND_TENANT_FRIENDLY_NAME,
      footer: general.footer,
      dashboardClaimsLinks: general.dashboardClaimsLinks.filter(linkFilterFunction).map((l: ILinkConfigSpecification) => LinkConfigFactory.create(l)),
      rebrokeText: general.rebrokeText,
      headerText: general.headerText,
      socialLinks: general.socialLinks.filter(linkFilterFunction).map((l: ILinkConfigSpecification) => LinkConfigFactory.create(l)),
      headerLogoPath: general.headerLogoPath,
      logoUrl: general.logoUrl,
      footerLogoPath: general.footerLogoPath,
      headerAccountText: general.headerAccountText,
      editAccountText: general.editAccountText,
      accountPageText: general.accountPageText,
      accountPageAdditionalInformation: general.accountPageAdditionalInformation,
      renewalEntryPageIndex: journey.renewalEntryPageIndex,
      aggregatorEditRoute: agg.aggregatorEditRoute as Routes,
      footerLinks: general.footerLinks.filter(linkFilterFunction).map((l: ILinkConfigSpecification) => LinkConfigFactory.create(l)),
      policyTakeupNextAddress: journey.policyTakeupNextAddress,
      specialButton: journey.specialButton.length > 0 && (linkFilterFunction(journey.specialButton[0])) ? LinkConfigFactory.create(journey.specialButton[0]) : null,
      alternateChangeDirectDebitPaymentDate(policy: Policy) {
        const specs = general.alternateChangeDirectDebitPaymentDate.filter(l => (!l.showForScheme) || (l.showForScheme && l.showForScheme.toLowerCase() === policy.scheme.name.toLowerCase()))
          .filter(linkFilterFunction)
          .map((l: ILinkConfigSpecification) => LinkConfigFactory.create(l, policy))
        return specs
      },
      features: general.features,
      notification: general.notification,
      defaultPaymentType: journey.defaultPaymentType as PaymentTypeEnum,
      notificationBannerText: general.notificationBannerText,
      renewalOptOutModal(product: ProductEnum, policy: Policy): LinkConfig {
        return LinkConfigFactory.create(journey.renewalOptOutModal[product], policy)
      },
      skipAggregatorLandingPage: agg.skipAggregatorLandingPage,
      trustPilot: general.trustPilot,
      accountHeaderActions: general.accountHeaderActions.filter(linkFilterFunction).map((l: ILinkConfigSpecification) => LinkConfigFactory.create(l)),
      referralText: general.referralText,
      renewalDateText: general.renewalDateText,
      cookiePolicyNotice: general.cookiePolicyNotice,
      showCantAfford: general.showCantAfford,
      abiDumpCodeFormat: general.abiDumpCodeFormat,
      aggregatorLandingForProduct(product: ProductEnum): string | null {
        return agg.aggregatorLandingForProduct[product]
      },
      paymentSummaryAdditionalProducts: journey.paymentSummaryAdditionalProducts,
      navLinkForAdditionalProduct(product: ProductEnum): string | null {
        return journey.navLinkForAdditionalProduct?.[product] ?? journey.insureProductAction[product]?.label ?? null
      },
      docsPreference(product: ProductEnum): IDocsPreference {
        return general.docsPreference[product] ?? general.docsPreference.default
      },
      policyType(schemeName: string) {
        const policyTypeConfig = general.policyType.find(pt => pt.schemeName === schemeName) ?? general.policyType.find(pt => !pt.schemeName)
        return policyTypeConfig?.policyType
      },
      insureProductAction(product: ProductEnum): { label: string, overrideUrl: string | null } | null {
        return journey.insureProductAction[product] ?? null
      },
      companyNameForProduct(product: ProductEnum): string | null {
        return journey.companyNameForProduct[product]
      },
      footerTextForProduct(product: ProductEnum): string | null {
        return journey.footerTextForProduct[product]
      },
      logoUrlForProduct(product: ProductEnum): string | null {
        return journey.logoUrlForProduct[product]
      },
      headerLogoPathForProduct(product: ProductEnum): string | null {
        return journey.headerLogoPathForProduct[product]
      },
      footerLogoPathForProduct(product: ProductEnum): string | null {
        return journey.footerLogoPathForProduct[product]
      },
      footerLinksForProduct(product: ProductEnum): LinkConfig[] | null {
        return journey.footerLinksForProduct![product].filter(linkFilterFunction).map((l: ILinkConfigSpecification) => LinkConfigFactory.create(l))
      },
      stepsForProduct(product: ProductEnum, route: Route): Step[] {
        const productSteps = journey.stepsForProduct[product]
        return productSteps.find(s => !s.matches || s.matches.find(m => m.toLowerCase() === route.name!.toLowerCase() || route.matched.find(rm => rm.name === m.toLowerCase())) || s.matches.length < 1)?.steps.map(s => new Step(s.label, s.icon, s.routes.map(r => r as Routes), s.pack)) ?? []
      },
      actionsForPolicy(policy: PolicySummary | Policy, options?: { ignoreUnavailableActions: boolean }): LinkConfig[] {
        const specs = general.actionsForPolicy[policy.product].filter(z => (!z.linkConfig!.showOnlyForRenewalStatus) || (z.linkConfig!.showOnlyForRenewalStatus && z.linkConfig!.showOnlyForRenewalStatus.includes(policy.renewalStatus ?? RenewalStatusEnum.notSet)))
          .filter(pa => ((policy.availableActions.some(aa => !pa.matches || pa.matches.some(m => m.toLowerCase() === aa.toLowerCase())) && !pa.linkConfig!.showWhenActionIsNotAvailable))
            || (!options?.ignoreUnavailableActions && !policy.availableActions.some(aa => !pa.matches || pa.matches.some(m => m.toLowerCase() === aa.toLowerCase())) && pa.linkConfig!.showWhenActionIsNotAvailable))
          .filter(a => !a.linkConfig || linkFilterFunction(a.linkConfig))
          .map(a => LinkConfigFactory.create(a.linkConfig!, policy))
        return specs
      },
      claimsLinks(policy: PolicySummary): LinkConfig[] {
        const specs = general.claimsLinks[policy.product].filter(l => (!l.showForVap && !l.showForScheme) || (l.showForScheme && l.showForScheme.toLowerCase() === policy.schemeName?.toLowerCase()) || (l.showForVap && policy.vaps.some(v => v.toLowerCase() === l.showForVap?.toLowerCase())))
          .filter(linkFilterFunction)
          .map((l: ILinkConfigSpecification) => LinkConfigFactory.create(l, policy))
        return specs
      },
      viewClaimsLinks(policy: PolicySummary, claim: ClaimSummary): LinkConfig[] {
        const specsSchemeSpecific = general.viewClaimsLinks.filter(l => l.showForScheme && l.showForScheme.toLowerCase() === policy.schemeName?.toLowerCase())
          .filter(linkFilterFunction)
          .map((l: ILinkConfigSpecification) => LinkConfigFactory.create(l, policy, claim))
        const specs = specsSchemeSpecific.length > 0
          ? specsSchemeSpecific
          : general.viewClaimsLinks.filter(l => !l.showForScheme)
              .filter(linkFilterFunction)
              .map((l: ILinkConfigSpecification) => LinkConfigFactory.create(l, policy, claim))
        return specs
      },
      tierFeatures(product: ProductEnum): TierFeature[] {
        const productTiers = general.tierFeatures[product]
        if (!productTiers)
          return []

        const specs: TierFeature[] = []

        for (const tierKey in productTiers) {
          if (!(tierKey in TierEnum))
            continue

          productTiers[tierKey].forEach((feature) => {
            return specs.push(new TierFeature(
              tierKey as TierEnum,
              feature.title,
              feature.type,
              feature.type === TierFeatureType.Expression ? feature.expressionKey : undefined,
              feature.type === TierFeatureType.Checkbox ? feature.checkboxValue : undefined,
              feature.transactionType,
              feature.text,
            ))
          })
        }

        return specs
      },
      errorMessageForCode(code: number): string {
        return general.errorMessageForCode[code.toString()]
      },
      effectiveFutureDateRestrictionDays(product: ProductEnum): number {
        return general.effectiveFutureDateRestrictionDays[product] ?? 30
      },
      directDebitPopUp(product: ProductEnum): IDirectDebitPopUpSpecification {
        return journey.directDebitPopUp[product]
      },
      documentsDeliveryPreferenceModal: journey.documentsDeliveryPreferenceModal,
      footerAlignment: general.footerAlignment,
      paymentMessages: journey.paymentMessages,
      paymentIcons: journey.paymentIcons,
      paymentRenewalInformation: journey.paymentRenewalInformation,
      directDebitInformation(schemeName: string, hasDeposit: boolean): string {
        const ddMarkdown = journey.directDebitInformation.filter(
          l => l.EnabledForSchemeName && l.EnabledForSchemeName.toLowerCase() === schemeName.toLowerCase(),
        )

        if (ddMarkdown.length > 0) {
          const ddMarkdownForDeposit = ddMarkdown.find(l => l.EnabledForDeposit === hasDeposit)
          if (ddMarkdownForDeposit)
            return ddMarkdownForDeposit.MarkdownText

          const ddMarkdownForScheme = ddMarkdown.find(l => !l.EnabledForDeposit)
          if (ddMarkdownForScheme)
            return ddMarkdownForScheme.MarkdownText
        }

        const ddMarkdownForDepositOnly = journey.directDebitInformation.find(l => l.EnabledForDeposit === hasDeposit && !l.EnabledForSchemeName)
        if (ddMarkdownForDepositOnly)
          return ddMarkdownForDepositOnly.MarkdownText

        return journey.directDebitInformation.find(l => !l.EnabledForSchemeName && !l.EnabledForDeposit)?.MarkdownText ?? ''
      },
      installAppText: general.installAppText,
      collapseDocsEndorsementsTables: journey.collapseDocsEndorsementsTables,
      collapseDocsEndorsementsTablesAtDesktop: journey.collapseDocsEndorsementsTablesAtDesktop,
      experiments,
      hideBillingAddress(): boolean {
        if (general.ignitePaymentGateway && general.ignitePaymentGateway.enabled)

          return general.ignitePaymentGateway.presentationMethod === 2

        return journey.hideBillingAddress
      },
      hidePaymentCoverSummary: journey.hidePaymentCoverSummary,
      showPaymentSummary: journey.showPaymentSummary,
      hidePaymentPagePolicySummary: journey.hidePaymentPagePolicySummary,
      showRemoveAll: journey.showRemoveAll,
      nbDeclineMessage: journey.nbDeclineMessage,
      mostPopularVap: journey.mostPopularVap,
      mtaRenewalDeclineMessage: journey.mtaRenewalDeclineMessage,
      veriskEnabled: general.veriskEnabled,
      ignitePaymentGateway: general.ignitePaymentGateway,
      blockMtaIfInArrears: general.blockMtaIfInArrears,
      goToFirstPageOnPremiumPreviousClick: journey.goToFirstPageOnPremiumPreviousClick,
      brochureSite: journey.brochureSite,
      renewalDiscount: general.renewalDiscount,
      claimsAndConvictions: journey.claimsAndConvictions,
      paymentPageNotice: journey.paymentPageNotice,
      showCoverLevelDetailOnSchemeCard: journey.showCoverLevelDetailOnSchemeCard,
      showPolicyDetailsOnSchemeCard: journey.showPolicyDetailsOnSchemeCard,
      mtaShowAbandonChangeConfirmation: journey.mtaShowAbandonChangeConfirmation,
      showDocumentsPopup: journey.showDocumentsPopup,
      alternatePremiumTitleText: journey.alternatePremiumTitleText,
      alternateRenewalPremiumTitleText: journey.alternateRenewalPremiumTitleText,
      hideCoverLevelDetailFeatures: journey.hideCoverLevelDetailFeatures,
      loginHelpText: general.loginHelpText,
      mtaVapSelectionInformation: journey.mtaVapSelectionInformation,
      goCardlessRenewalText: general.goCardlessRenewalText,
      goCardlessMonthlyPaymentWithDepositText: general.goCardlessMonthlyPaymentWithDepositText,
      goCardlessMonthlyPaymentWithoutDepositText: general.goCardlessMonthlyPaymentWithoutDepositText,
      premiumPageHideNext: general.premiumPageHideNext,
      newBusinessPremiumPageNextClickUpdatesQuote: journey.newBusinessPremiumPageNextClickUpdatesQuote,
      showVapIpt: journey.showVapIpt,
      showPremiumOnVapPage: journey.showPremiumOnVapPage,
      summaryPageTooltips: journey.summaryPageTooltips,
      benefitsDashboardItem: general.benefitsDashboardItem,
      alternateAPMTAPremiumHeader: journey.alternateAPMTAPremiumHeader,
      alternateAddNewProductMarkup: general.alternateAddNewProductMarkup,
      authServiceHelpText: general.authServiceHelpText,
      readOnlyClaimsPostNewBusiness: general.readOnlyClaimsPostNewBusiness,
      nonEditableClaimsText: general.nonEditableClaimsText,
      maxVehiclePurchaseDate: general.maxVehiclePurchaseDate,
      showCancellationRetentionPageForProduct(product: ProductEnum): boolean | null {
        return journey.showCancellationRetentionPageForProduct?.[product] ?? null
      },
      newCustomerPortalForProduct(product: ProductEnum): boolean {
        return journey.newCustomerPortalForProduct[product] ?? false
      },
      newCustomerPortalResumeQuotePage: journey.newCustomerPortalResumeQuotePage ?? 'premium',
      grantedCoPayOnMtaPremiumPage: journey.grantedCoPayOnMtaPremiumPage,
      managePolicyAdditionalInformation(policy: Policy): string | null {
        if (general.managePolicyAdditionalInformation.showForScheme) {
          return general.managePolicyAdditionalInformation.showForScheme.toLowerCase() === policy.scheme.name.toLowerCase()
            ? general.managePolicyAdditionalInformation.markdown
            : null
        }

        return general.managePolicyAdditionalInformation.markdown
      },
      manageClaimsEnabled: general.manageClaimsEnabled,
      manageClaimsEnabledForProductSection(product: ProductEnum): boolean {
        return general.manageClaimsEnabledForProductSection[product] ?? false
      },
      goCardlessInstantPaymentEnabled: general.goCardlessInstantPaymentEnabled ?? false,
      referAFriend: {
        enabled: general.referAFriend?.enabled ?? false,
        defaultProduct: general.referAFriend?.defaultProduct,
        text: general.referAFriend?.text,
      },
      customHomeBanner: {
        enabled: general.customHomeBanner.enabled,
        markdown: general.customHomeBanner.markdown,
        linkConfig: general.customHomeBanner.linkConfig ? LinkConfigFactory.create(general.customHomeBanner.linkConfig) : null,
        onlyShowWhen3YPFPolicyAvailable: general.customHomeBanner.onlyShowWhen3YPFPolicyAvailable,
      },
      threeYearPriceFixOptIn: journey.threeYearPriceFixOptIn,
      renewalPremiumPageBanner: {
        enabled: journey.renewalPremiumPageBanner.enabled,
        markdown: journey.renewalPremiumPageBanner.markdown,
        linkConfig: journey.renewalPremiumPageBanner.linkConfig ? LinkConfigFactory.create(journey.renewalPremiumPageBanner.linkConfig) : null,
        onlyShowWhen3YPFPolicyAvailable: journey.renewalPremiumPageBanner.onlyShowWhen3YPFPolicyAvailable,
      },
    }
  }
}
